import type { GroupBy } from '../common/types.js';
import { t } from '../logical/index.js';
import type { TypeInstance } from '../logical/type.js';
import { compareTypedValues } from '../sort/eval.js';
import { compareDateKeys } from './compare-date-keys.js';

export type GroupSortMode = 'default' | 'asc' | 'desc';

export const getGroupSortMode = (
  groupBy: GroupBy | undefined
): GroupSortMode => {
  const sort = groupBy?.sort;
  return sort == null ? 'default' : sort.desc ? 'desc' : 'asc';
};

export const withGroupSortMode = (
  groupBy: GroupBy,
  mode: GroupSortMode
): GroupBy => {
  const { sort: _sort, ...rest } = groupBy;
  return mode === 'default'
    ? rest
    : {
        ...rest,
        sort: { desc: mode === 'desc' },
      };
};

/** Multi-select grouping emits one scalar option id per group. */
export const resolveGroupValueType = (type: TypeInstance): TypeInstance =>
  t.array.is(type) ? type.element : type;

export const isGroupSortSupported = (type: TypeInstance): boolean => {
  if (t.array.is(type)) {
    return t.tag.is(type.element);
  }
  const valueType = resolveGroupValueType(type);
  return (
    t.string.is(valueType) ||
    t.number.is(valueType) ||
    t.date.is(valueType) ||
    t.boolean.is(valueType) ||
    t.tag.is(valueType)
  );
};

const applyManualOrder = (keys: string[], manualKeys: readonly string[]) => {
  const values = new Set(keys);
  const ordered = manualKeys.filter(key => values.delete(key));
  return [...ordered, ...keys.filter(key => values.has(key))];
};

export const sortGroupKeys = ({
  keys,
  valueForKey,
  valueType,
  mode,
  manualKeys = [],
  dateMode,
}: {
  keys: string[];
  valueForKey: (key: string) => unknown;
  valueType: TypeInstance;
  mode: GroupSortMode;
  manualKeys?: readonly string[];
  dateMode?: string;
}) => {
  const dateAsc = dateMode
    ? [...keys].sort(compareDateKeys(dateMode, true))
    : [...keys];
  if (mode === 'default') {
    return applyManualOrder(dateAsc, manualKeys);
  }

  const manualRank = new Map(manualKeys.map((key, index) => [key, index]));
  const inputRank = new Map(keys.map((key, index) => [key, index]));
  const desc = mode === 'desc';
  const dateComparator = dateMode
    ? compareDateKeys(dateMode, !desc)
    : undefined;

  return [...keys].sort((a, b) => {
    const result = dateComparator
      ? dateComparator(a, b)
      : compareTypedValues(valueType, valueForKey(a), valueForKey(b), desc);
    if (result !== 0) return result;

    const rankA = manualRank.get(a);
    const rankB = manualRank.get(b);
    if (rankA != null && rankB != null) return rankA - rankB;
    if (rankA != null) return -1;
    if (rankB != null) return 1;
    const inputResult = (inputRank.get(a) ?? 0) - (inputRank.get(b) ?? 0);
    return inputResult || a.localeCompare(b);
  });
};
